<!--
 @description Standalone dialog informing the user they have reached the
 maximum number of blacklisted challenges. Dismissable only by the single
 OK button — backdrop click is explicitly disabled (isOutsideClosable=false)
 and the × close icon is hidden (isClosable=false), preventing accidental
 dismissal (AC-12, AC-18, AC-06).

 Refactored from a native <dialog> root to wrap content inside the shared
 Modal.vue primitive (DEC-031). This ensures consistent backdrop chrome, ESC
 handling, and accessibility wiring across the app's modal surfaces.

 Used in slice 4 (browse list) and slice 5 (in-game SideMenu).

 @props
   isOpen — Controls dialog visibility; forwarded to the inner Modal primitive.

 @emits
   close — Emitted when the OK button is tapped. The parent must set
           isOpen to false in response.
-->
<template>
  <Modal :isOpen="isOpen" :isOutsideClosable="false" :isClosable="false" @close="$emit('close')">
    <h2 class="cap-reached-dialog__title">
      {{ t('challengeBlacklist.capReachedTitle') }}
    </h2>

    <p class="cap-reached-dialog__body">
      {{ t('challengeBlacklist.capReachedBody') }}
    </p>

    <button class="cap-reached-dialog__ok-btn primary" @click="handleOk">
      {{ t('challengeBlacklist.capReachedButton') }}
    </button>
  </Modal>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import Modal from '@/components/common/Global/modal/Modal.vue'

// ---------------------------------------------------------------------------
// Props / emits
// ---------------------------------------------------------------------------

interface CapReachedDialogProps {
  /** Controls visibility of the dialog. Forwarded to the inner Modal primitive. */
  isOpen: boolean
}

defineProps<CapReachedDialogProps>()

const emit = defineEmits<{
  /** Emitted when the OK button is tapped. */
  close: []
}>()

// ---------------------------------------------------------------------------
// Composables
// ---------------------------------------------------------------------------

const { t } = useI18n()

// ---------------------------------------------------------------------------
// Handlers
// ---------------------------------------------------------------------------

/** Notifies the parent to close the dialog via the `close` event. */
function handleOk(): void {
  emit('close')
}
</script>

<style lang="scss">
.cap-reached-dialog {
  &__title {
    margin: 0 0 1rem;
  }

  &__body {
    margin: 0 0 1.5rem;
  }

  &__ok-btn {
    align-self: flex-end;
  }
}
</style>
